import {
  CUSTOM_ITEMS,
  CUSTOM_SKIN_ID,
  DEMO_ADD_QUERY,
  DEMO_ADVISOR,
  DEMO_BENCHMARK_QUERY,
  DEMO_COPY_NAME,
  DEMO_CUSTOM_LAYOUT_ID,
  DEMO_CUSTOM_LAYOUT_NAME,
  DEMO_DISCLAIMER,
  DEMO_INDEXES,
  DEMO_MISSING_ROW_ID,
  DEMO_PM_NAME,
  DEMO_STAT_ROWS,
  EDITED_STAT_ROWS,
  EXECUTIVE_ITEMS,
  MODERN_ITEMS,
  NEW_SKIN_DRAFT,
  LARKSPUR_SKIN,
  placeItem,
  STENCIL_LABELS,
  type DemoSkin,
  type GridItem,
  type SkinToken,
  type StencilId,
} from './demoData';
import type { LandingStageId } from './stages';

export type DemoView = 'reports' | 'builder' | 'branding' | 'create';
export type DemoWorkflowStatus = 'draft' | 'submitted' | 'approved' | 'published';
export type DemoUpload = 'empty' | 'uploading' | 'done';

export type DemoState = {
  view: DemoView;
  loading: boolean;
  statusFilter: 'all' | 'missing';
  statusMenuOpen: boolean;
  highlightRow: string | null;
  layoutId: string;
  layoutName: string;
  layoutMenuOpen: boolean;
  customLayout: boolean;
  items: GridItem[];
  selected: StencilId | null;
  hoverCard: StencilId | null;
  hoverCell: string | null;
  dragging: StencilId | null;
  dropPreview: Omit<GridItem, 'id'> | null;
  flash: StencilId | null;
  sizeMenu: StencilId | null;
  addModal: { query: string; picked: string } | null;
  editModal: { rows: string[] } | null;
  statRows: string[];
  skinId: string;
  customSkin: DemoSkin | null;
  skinModal: 'setup' | 'create' | null;
  skinDraft: DemoSkin;
  hexTyping: { token: SkinToken; text: string } | null;
  favorites: string[];
  favoritesModal: string[] | null;
  dirty: boolean;
  saving: string | null;
  typing: string | null;
  font: string;
  fontMenuOpen: boolean;
  bold: boolean;
  advisor: string;
  logo: DemoUpload;
  disclaimer: string;
  benchmarks: string[];
  benchQuery: string;
  pmPhoto: DemoUpload;
  pmName: string;
  fundMenuOpen: boolean;
  fundPicked: boolean;
  status: DemoWorkflowStatus | null;
  busy: boolean;
  pdf: 'closed' | 'open' | 'rendering' | 'done';
  pdfMode: 'continuous' | 'a4';
  pdfProgress: number;
  toast: string | null;
};

/** Menus, modals, and in-flight flags — cleared whenever the player seeks. */
const TRANSIENT: Partial<DemoState> = {
  loading: false,
  statusMenuOpen: false,
  layoutMenuOpen: false,
  selected: null,
  hoverCard: null,
  hoverCell: null,
  dragging: null,
  dropPreview: null,
  flash: null,
  sizeMenu: null,
  addModal: null,
  editModal: null,
  skinModal: null,
  hexTyping: null,
  favoritesModal: null,
  saving: null,
  typing: null,
  fontMenuOpen: false,
  benchQuery: '',
  fundMenuOpen: false,
  busy: false,
  pdf: 'closed',
  pdfProgress: 0,
  toast: null,
};

export const INITIAL_DEMO_STATE: DemoState = {
  view: 'reports',
  loading: true,
  statusFilter: 'all',
  statusMenuOpen: false,
  highlightRow: null,
  layoutId: 'executive',
  layoutName: 'Executive',
  layoutMenuOpen: false,
  customLayout: false,
  items: EXECUTIVE_ITEMS,
  selected: null,
  hoverCard: null,
  hoverCell: null,
  dragging: null,
  dropPreview: null,
  flash: null,
  sizeMenu: null,
  addModal: null,
  editModal: null,
  statRows: DEMO_STAT_ROWS,
  skinId: 'institutional',
  customSkin: null,
  skinModal: null,
  skinDraft: NEW_SKIN_DRAFT,
  hexTyping: null,
  favorites: ['executive', 'institutional', 'modern'],
  favoritesModal: null,
  dirty: false,
  saving: null,
  typing: null,
  font: 'arial',
  fontMenuOpen: false,
  bold: false,
  advisor: 'Example Advisor',
  logo: 'empty',
  disclaimer: 'For illustration only — seed data.',
  benchmarks: ['MSCI World Index'],
  benchQuery: '',
  pmPhoto: 'empty',
  pmName: '',
  fundMenuOpen: false,
  fundPicked: false,
  status: null,
  busy: false,
  pdf: 'closed',
  pdfMode: 'continuous',
  pdfProgress: 0,
  toast: null,
};

export type DemoPatch = Partial<DemoState> | ((state: DemoState) => Partial<DemoState>);

/** Cursor + state primitives the landing player implements. Missing targets never block the script. */
export type TourDriver = {
  say: (subtitle: string) => void;
  set: (patch: DemoPatch) => void;
  wait: (ms: number) => Promise<void>;
  move: (target: string, ms?: number) => Promise<void>;
  click: (target: string, patch?: DemoPatch) => Promise<void>;
  drag: (from: string, to: string, label: string, steps: { grab: DemoPatch; hover: DemoPatch; drop: DemoPatch }) => Promise<void>;
  type: (target: string, text: string, apply: (typed: string) => DemoPatch) => Promise<void>;
  reveal: (target: string | null) => Promise<void>;
};

async function typeInto(t: TourDriver, target: string, text: string, apply: (typed: string) => DemoPatch) {
  t.set({ typing: target });
  await t.type(target, text, apply);
  t.set({ typing: null });
}

async function save(t: TourDriver, target: string, key: string, toast: string, extra: Partial<DemoState> = {}) {
  await t.click(target, { saving: key, toast: null });
  await t.wait(800);
  t.set({ saving: null, toast, ...extra });
  await t.wait(1000);
}

async function busyStep(t: TourDriver, target: string, ms: number, done: Partial<DemoState>) {
  await t.click(target, { busy: true, toast: null });
  await t.wait(ms);
  t.set({ busy: false, ...done });
  await t.wait(1000);
}

async function reports(t: TourDriver) {
  t.say('Loading funds for the last seven months.');
  await t.wait(1100);
  t.set({ loading: false });
  t.say('Each fund card lists months, status, and the next action.');
  await t.wait(1600);
  t.say('Open the Status filter.');
  await t.click('filter-status', { statusMenuOpen: true });
  await t.wait(700);
  await t.move('status-option-all', 500);
  t.say('Choose Missing.');
  await t.click('status-option-missing', { statusMenuOpen: false, statusFilter: 'missing', loading: true });
  await t.wait(500);
  t.set({ loading: false, highlightRow: DEMO_MISSING_ROW_ID });
  t.say('This month has no tearsheet yet. Create will start it. Next, open Layout Builder.');
  await t.wait(1400);
  await t.move('row-create-missing');
  await t.wait(900);
}

async function grid(t: TourDriver) {
  t.say('Layout Builder opens on the grid: four columns, fixed row tracks.');
  await t.click('tab-builder', { view: 'builder', loading: true, highlightRow: null, toast: null });
  await t.wait(800);
  t.set({ loading: false });
  await t.wait(900);
  t.say('Switch from Executive to the Modern stock layout.');
  await t.click('layout-select', { layoutMenuOpen: true });
  await t.wait(700);
  await t.click('layout-option-modern', { layoutMenuOpen: false, layoutId: 'modern', layoutName: 'Modern', items: MODERN_ITEMS, loading: true });
  await t.wait(500);
  t.set({ loading: false });
  await t.wait(1000);

  t.say('Hover an empty cell. A + appears. Click it to add a stencil.');
  await t.move('cell-6-3', 900);
  t.set({ hoverCell: '6-3' });
  await t.wait(700);
  await t.click('cell-6-3', { addModal: { query: '', picked: 'commentary' }, hoverCell: null });
  await t.wait(800);
  t.say('Search the stencil catalog.');
  await t.click('add-filter');
  await typeInto(t, 'add-filter', DEMO_ADD_QUERY, (query) => (s) => ({ addModal: { query, picked: s.addModal?.picked ?? 'drawdowns' } }));
  await t.wait(400);
  await t.click('add-item-drawdowns', (s) => ({ addModal: { query: s.addModal?.query ?? DEMO_ADD_QUERY, picked: 'drawdowns' } }));
  t.say('Preview Drawdown Table, then add it to the layout.');
  await t.wait(1300);
  await t.click('add-confirm', (s) => ({
    addModal: null,
    items: [...s.items, { id: 'drawdowns', col: 3, row: 6, cols: 1, rows: 3 }],
    flash: 'drawdowns',
    dirty: true,
  }));
  await t.wait(1000);

  t.say('Resize it: open Size on the card and choose 1/2.');
  await t.click('stencil-drawdowns', { selected: 'drawdowns', flash: null });
  await t.wait(500);
  await t.click('size-drawdowns', { sizeMenu: 'drawdowns' });
  await t.wait(600);
  await t.click('size-option-2', (s) => ({ sizeMenu: null, items: placeItem(s.items, 'drawdowns', { cols: 2 }), flash: 'drawdowns' }));
  await t.wait(1100);

  t.say('Drag and drop: move Program Description to the left of VAMI.');
  await t.move('stencil-commentary', 800);
  t.set({ selected: null, hoverCard: 'commentary', flash: null });
  await t.wait(700);
  await t.drag('stencil-commentary', 'stencil-vami', STENCIL_LABELS.commentary, {
    grab: { dragging: 'commentary', hoverCard: null },
    hover: { dropPreview: { col: 1, row: 3, cols: 2, rows: 3 } },
    drop: (s) => ({
      items: placeItem(placeItem(s.items, 'commentary', { col: 1 }), 'vami', { col: 3 }),
      dragging: null,
      dropPreview: null,
      flash: 'commentary',
    }),
  });
  t.say('VAMI slides over to make room.');
  await t.wait(1200);
}

async function stencil(t: TourDriver) {
  t.say('Select Performance Statistics and open its editor.');
  await t.click('stencil-risk', { selected: 'risk', flash: null });
  await t.wait(600);
  await t.click('edit-risk', (s) => ({ editModal: { rows: s.statRows } }));
  await t.wait(900);
  t.say('Pick which statistics appear — drop YTD and Avg. Monthly Return…');
  const toggle = (label: string) => (s: DemoState) => {
    const rows = s.editModal?.rows ?? s.statRows;
    return { editModal: { rows: rows.includes(label) ? rows.filter((row) => row !== label) : [...rows, label] } };
  };
  await t.click('stat-row-YTD', toggle('YTD'));
  await t.wait(350);
  await t.click('stat-row-Avg. Monthly Return', toggle('Avg. Monthly Return'));
  await t.wait(350);
  t.say('…add Max. Drawdown and Calmar Ratio.');
  await t.click('stat-row-Max. Drawdown', toggle('Max. Drawdown'));
  await t.wait(350);
  await t.click('stat-row-Calmar Ratio', toggle('Calmar Ratio'));
  await t.wait(700);
  await t.click('edit-apply', (s) => ({ editModal: null, statRows: s.editModal?.rows ?? s.statRows, flash: 'risk' }));
  t.say('The card updates in place.');
  await t.wait(1400);
}

async function skin(t: TourDriver) {
  t.say('Stock skins recolor the whole page instantly.');
  await t.click('skin-black_orange', { skinId: 'black_orange', selected: null, flash: null });
  await t.wait(1000);
  await t.click('skin-slate', { skinId: 'slate' });
  await t.wait(900);
  t.say('Or build your own: Edit menu → Create new.');
  await t.click('skin-edit-menu', { skinModal: 'setup' });
  await t.wait(1000);
  await t.click('skin-tab-create', { skinModal: 'create', skinDraft: NEW_SKIN_DRAFT });
  await t.wait(800);
  await t.click('skin-name', (s) => ({ skinDraft: { ...s.skinDraft, name: '' } }));
  await typeInto(t, 'skin-name', LARKSPUR_SKIN.name, (name) => (s) => ({ skinDraft: { ...s.skinDraft, name } }));
  t.say('Set each color by hex — the preview follows along.');
  const tokens: SkinToken[] = ['bg', 'panel', 'text', 'accent'];
  for (const token of tokens) {
    await t.click(`skin-hex-${token}`, { hexTyping: { token, text: '' } });
    await typeInto(t, `skin-hex-${token}`, LARKSPUR_SKIN[token], (text) => ({ hexTyping: { token, text } }));
    t.set((s) => ({ hexTyping: null, skinDraft: { ...s.skinDraft, [token]: LARKSPUR_SKIN[token] } }));
    await t.wait(350);
  }
  t.set((s) => ({ skinDraft: { ...s.skinDraft, panel2: LARKSPUR_SKIN.panel2, muted: LARKSPUR_SKIN.muted, border: LARKSPUR_SKIN.border } }));
  await t.wait(900);
  await t.click('skin-save-close', { saving: 'skin' });
  await t.wait(800);
  t.set((s) => ({ saving: null, skinModal: null, customSkin: s.skinDraft, skinId: CUSTOM_SKIN_ID, toast: 'Skin saved', dirty: true }));
  t.say('Your skin is pinned to the toolbar and applied.');
  await t.wait(1500);
}

async function layout(t: TourDriver) {
  t.say('Stock layouts are read-only — Copy as makes your own.');
  await t.click('copy-as', { layoutName: DEMO_COPY_NAME, customLayout: true, layoutId: DEMO_CUSTOM_LAYOUT_ID, toast: 'Layout duplicated' });
  await t.wait(900);
  t.say('Give it a name…');
  await t.click('layout-name', { layoutName: '', toast: null });
  await typeInto(t, 'layout-name', DEMO_CUSTOM_LAYOUT_NAME, (layoutName) => ({ layoutName }));
  await t.wait(400);
  await save(t, 'save-layout', 'layout', 'Layout saved', { dirty: false });
  t.say('…and pin it to your favorites: Edit favorites.');
  await t.click('edit-favorites', (s) => ({ favoritesModal: s.favorites, toast: null }));
  await t.wait(1000);
  t.say('All three slots are full — free one, then pick your new layout.');
  await t.click('fav-clear-1', (s) => ({ favoritesModal: (s.favoritesModal ?? s.favorites).map((id, i) => (i === 1 ? '' : id)) }));
  await t.wait(700);
  await t.click(`fav-card-${DEMO_CUSTOM_LAYOUT_ID}`, (s) => ({
    favoritesModal: (s.favoritesModal ?? s.favorites).map((id) => (id ? id : DEMO_CUSTOM_LAYOUT_ID)),
  }));
  await t.wait(900);
  await t.click('fav-save-close', { saving: 'favorites' });
  await t.wait(700);
  t.set((s) => ({ saving: null, favorites: s.favoritesModal ?? s.favorites, favoritesModal: null, toast: 'Favorites updated' }));
  await t.wait(1300);
}

async function branding(t: TourDriver) {
  t.say('Branding applies to every tearsheet for this datasource.');
  await t.click('tab-branding', { view: 'branding', loading: true, toast: null, selected: null });
  await t.wait(800);
  t.set({ loading: false });
  await t.wait(700);
  t.say('Brand identity: pick a font and weight.');
  await t.click('font-select', { fontMenuOpen: true });
  await t.wait(600);
  await t.click('font-option-georgia', { fontMenuOpen: false, font: 'georgia' });
  await t.wait(400);
  await t.click('bold-switch', { bold: true });
  await t.wait(400);
  await save(t, 'fonts-save', 'fonts', 'Branding saved');
  t.say('Header and disclaimer: advisor name, company logo, regulatory text.');
  await t.click('advisor-field', { advisor: '', toast: null });
  await typeInto(t, 'advisor-field', DEMO_ADVISOR, (advisor) => ({ advisor }));
  await t.click('logo-upload', { logo: 'uploading' });
  await t.wait(1000);
  t.set({ logo: 'done' });
  await t.wait(500);
  await t.click('disclaimer-field', { disclaimer: '' });
  await typeInto(t, 'disclaimer-field', DEMO_DISCLAIMER.slice(0, 48), (disclaimer) => ({ disclaimer }));
  t.set({ disclaimer: DEMO_DISCLAIMER });
  await save(t, 'header-save', 'header', 'Branding saved');
  t.say('Benchmarks: search an index and add it.');
  await t.reveal('group-benchmarks');
  await t.click('bench-search', { toast: null });
  await typeInto(t, 'bench-search', DEMO_BENCHMARK_QUERY, (benchQuery) => ({ benchQuery }));
  await t.wait(600);
  await t.click('bench-option-0', (s) => ({ benchmarks: [...s.benchmarks, DEMO_INDEXES[0]!], benchQuery: '' }));
  await save(t, 'bench-save', 'benchmarks', 'Branding saved');
  t.say('Portfolio manager: photo and name.');
  await t.reveal('group-pm');
  await t.click('pm-photo-upload', { pmPhoto: 'uploading', toast: null });
  await t.wait(1000);
  t.set({ pmPhoto: 'done' });
  await t.click('pm-name', { pmName: '' });
  await typeInto(t, 'pm-name', DEMO_PM_NAME, (pmName) => ({ pmName }));
  await save(t, 'pm-save', 'manager', 'Branding saved');
  await t.reveal(null);
  await t.wait(600);
}

async function create(t: TourDriver) {
  t.say('Create tearsheet: pick the fund with the missing month.');
  await t.click('tab-create', { view: 'create', toast: null });
  await t.wait(700);
  await t.click('create-fund', { fundMenuOpen: true });
  await t.wait(700);
  await t.click('fund-option-103', { fundMenuOpen: false, fundPicked: true, loading: true });
  t.say('It composes live with your favorite layout, custom skin, and branding.');
  await t.wait(1000);
  t.set({ loading: false });
  await t.wait(1200);
  await t.reveal('stencil-monthly');
  await t.wait(1300);
  await t.reveal(null);
  t.say('Save it as a draft.');
  await busyStep(t, 'wf-save', 800, { status: 'draft', toast: 'Draft saved' });
}

async function workflow(t: TourDriver) {
  t.say('Submit for review…');
  await busyStep(t, 'wf-submit', 600, { status: 'submitted', toast: 'Submitted for review' });
  t.say('…a reviewer approves…');
  await busyStep(t, 'wf-approve', 600, { status: 'approved', toast: 'Approved' });
  t.say('…and it is published for clients.');
  await busyStep(t, 'wf-publish', 700, { status: 'published', toast: 'Published for clients' });
}

async function pdf(t: TourDriver) {
  t.say('Export to PDF and choose the paper.');
  await t.click('wf-export', { pdf: 'open', toast: null });
  await t.wait(800);
  await t.click('pdf-a4', { pdfMode: 'a4' });
  await t.wait(600);
  await t.click('pdf-generate', { pdf: 'rendering', pdfProgress: 0 });
  t.say('Paginated A4 keeps every stencil whole across pages.');
  for (let step = 1; step <= 10; step += 1) {
    await t.wait(200);
    t.set({ pdfProgress: step * 10 });
  }
  t.set({ pdf: 'done' });
  t.say('Two pages, ready to send.');
  await t.wait(1000);
  await t.move('pdf-open');
  await t.wait(2600);
}

export type TourStage = {
  id: LandingStageId;
  run: (t: TourDriver) => Promise<void>;
  /** Durable result, so seeking past this stage reproduces what it would have left behind. */
  outcome: Partial<DemoState>;
};

export const TOUR: readonly TourStage[] = [
  { id: 'reports', run: reports, outcome: { view: 'reports', statusFilter: 'missing', highlightRow: DEMO_MISSING_ROW_ID } },
  {
    id: 'grid',
    run: grid,
    outcome: { view: 'builder', layoutId: 'modern', layoutName: 'Modern', items: CUSTOM_ITEMS, dirty: true, highlightRow: null },
  },
  { id: 'stencil', run: stencil, outcome: { statRows: EDITED_STAT_ROWS } },
  { id: 'skin', run: skin, outcome: { skinId: CUSTOM_SKIN_ID, customSkin: LARKSPUR_SKIN, skinDraft: LARKSPUR_SKIN } },
  {
    id: 'layout',
    run: layout,
    outcome: {
      layoutId: DEMO_CUSTOM_LAYOUT_ID,
      layoutName: DEMO_CUSTOM_LAYOUT_NAME,
      customLayout: true,
      dirty: false,
      favorites: ['executive', DEMO_CUSTOM_LAYOUT_ID, 'modern'],
    },
  },
  {
    id: 'branding',
    run: branding,
    outcome: {
      view: 'branding',
      font: 'georgia',
      bold: true,
      advisor: DEMO_ADVISOR,
      logo: 'done',
      disclaimer: DEMO_DISCLAIMER,
      benchmarks: ['MSCI World Index', DEMO_INDEXES[0]!],
      pmPhoto: 'done',
      pmName: DEMO_PM_NAME,
    },
  },
  { id: 'create', run: create, outcome: { view: 'create', fundPicked: true, status: 'draft' } },
  { id: 'workflow', run: workflow, outcome: { status: 'published' } },
  { id: 'pdf', run: pdf, outcome: {} },
];

/** State at the first frame of `TOUR[index]` — initial seed plus every earlier stage's outcome. */
export function stageStartState(index: number): DemoState {
  const reached = TOUR.slice(0, index).reduce<DemoState>((state, stage) => ({ ...state, ...stage.outcome }), INITIAL_DEMO_STATE);
  return index === 0 ? INITIAL_DEMO_STATE : { ...reached, ...TRANSIENT };
}
